import React from 'react';
import { Dialog, DialogActions, DialogContent, Alert, AlertTitle } from '@mui/material';
import { StyledBodyText, StyledHeaderTextH2 } from '../../../../../common/Styleguide/Common/Text';
import { StyledButton } from '../../../../../common/Styleguide/Common/Button';
import { toast } from 'react-toastify';
import logo from '../../../../../common/img/game-jam-logo.svg';
import { GAMEJAM_LINK, GAMEJAM_LINK_DISPLAY, GameJamLink, getGameJamLink } from './gamejamHelper';
import ArrowLeft from '../../../../../common/icons/ArrowLeft';
import { ContentCopy, OpenInNew, WarningAmber } from '@mui/icons-material';

interface GameJamConfirmationProps {
  submissionId: string;
  goToSubmission: () => void;
}

const GameJamConfirmation: React.FC<GameJamConfirmationProps> = ({ submissionId, goToSubmission }) => {
  const link = getGameJamLink(submissionId);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  return (
    <Dialog open maxWidth={'md'}>
      <DialogContent>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img src={logo} style={{ height: 120 }} alt="Game Jam" />
        </div>
        <StyledHeaderTextH2>Thank You for Your Submission!</StyledHeaderTextH2>
        <StyledBodyText>
          Here is your shareable link:{' '}
          <GameJamLink href={link} target="_blank">
            {link}
          </GameJamLink>
        </StyledBodyText>
        <Alert icon={<WarningAmber sx={{ fontSize: 50 }} />} severity="warning" sx={{ fontSize: 18 }}>
          <AlertTitle color={'white'} sx={{ fontSize: 22 }}>
            Important next steps:
          </AlertTitle>
          <ul>
            <li>Test your game using the link above.</li>
            <li>
              Register your participation at{' '}
              <GameJamLink href={GAMEJAM_LINK} target="_blank" sx={{ color: 'white' }}>
                {GAMEJAM_LINK_DISPLAY}
              </GameJamLink>{' '}
              using this shareable link.
            </li>
          </ul>
        </Alert>
        <StyledBodyText>
          What You Need to Know:
          <ul>
            <li>Your game is currently only accessible as a preview via this link.</li>
            <li>It cannot be discovered on CrazyGames and does not generate revenue at this stage.</li>
            <li>You can update your submission anytime until the game jam ends (updates may take a few minutes to appear).</li>
            <li>
              Submitting your game to CrazyGames is not enough - you must also register at{' '}
              <GameJamLink href={GAMEJAM_LINK} target="_blank" sx={{ color: 'white' }}>
                {GAMEJAM_LINK_DISPLAY}
              </GameJamLink>
              .
            </li>
          </ul>
        </StyledBodyText>
        <StyledBodyText>
          Deadline Information:
          <ul>
            <li>Your preview link remains the same even after updates.</li>
            <li>
              To ensure validating your participation:
              <ul>
                <li>
                  Register your participation well before the deadline, you'll still be able to update your game here until last minute.
                </li>
                <li>Regularly update and test your game here.</li>
                <li>
                  Some Unity build options can considerably increase the build time, so don’t wait until the last minute to build your final
                  result.
                </li>
              </ul>
            </li>
            <li>You cannot update your game or register after the game jam ends.</li>
            <li>
              The deadline (<b>11:59PM CET on May 5</b>) is automatic and no exceptions will be made.
            </li>
          </ul>
        </StyledBodyText>
        <StyledBodyText>
          Note: Your game will not be published on CrazyGames immediately. However, our team is always on the lookout for great games. We
          will be reviewing all game jam submissions, and if we determine that your game has commercial potential, we will reach out with
          further instructions in the coming weeks.
        </StyledBodyText>
      </DialogContent>
      <DialogActions sx={{ py: 3 }}>
        <StyledButton onClick={goToSubmission} variant="contained" color="white" height={50} sx={{ minWidth: 200, mr: 1 }}>
          <ArrowLeft />
          Back to submission
        </StyledButton>
        <StyledButton onClick={handleCopyLink} variant="contained" color="white" height={50} sx={{ minWidth: 200 }}>
          <ContentCopy />
          Copy link
        </StyledButton>
        <a href={link} target="_blank" rel="noreferrer">
          <StyledButton variant="contained" color="purple" height={50} sx={{ minWidth: 200 }}>
            <OpenInNew />
            Preview your game
          </StyledButton>
        </a>
      </DialogActions>
    </Dialog>
  );
};

export default GameJamConfirmation;
